import { useCallback, useEffect, useState } from 'react';
import { ApiError, direccionApi, type OperarioTerminal } from '../lib/api';
import { Dialog, Empty, Field, Pill } from '../components/ui';

/**
 * Datos maestros del terminal.
 *
 * Todo lo de esta pantalla se escribe EN SAP, que sigue siendo la única fuente
 * de la verdad. Quien prefiera hacerlo desde el cliente de SAP puede seguir
 * haciéndolo: el terminal lo recoge igual.
 */
export function Maestros({ puedeGestionar }: { puedeGestionar: boolean }) {
  const [seccion, setSeccion] = useState<'recursos' | 'fases' | 'causas' | 'historico'>('recursos');
  return (
    <div className="stack">
      <div className="chips">
        {([['recursos', 'Recursos y roles'], ['fases', 'Fases'], ['causas', 'Causas de parada'], ['historico', 'Histórico']] as const).map(([k, l]) => (
          <button key={k} className={`chip${seccion === k ? ' chip--selected' : ''}`} onClick={() => setSeccion(k)}>
            {l}
          </button>
        ))}
      </div>
      {seccion === 'recursos' ? <Recursos puedeGestionar={puedeGestionar} /> : null}
      {seccion === 'fases' ? <Fases puedeGestionar={puedeGestionar} /> : null}
      {seccion === 'causas' ? <Causas puedeGestionar={puedeGestionar} /> : null}
      {seccion === 'historico' ? <Historico puedeGestionar={puedeGestionar} /> : null}
    </div>
  );
}

/**
 * Quién trabaja con cada recurso de mano de obra.
 *
 * Es lo que decide en qué fases puede imputar cada operario, y era lo único
 * que obligaba a entrar en SAP para poner a alguien a trabajar.
 */
function Recursos({ puedeGestionar }: { puedeGestionar: boolean }) {
  const [datos, setDatos] = useState<Awaited<ReturnType<typeof direccionApi.recursos>> | null>(null);
  const [empleados, setEmpleados] = useState<OperarioTerminal[]>([]);
  const [busqueda, setBusqueda] = useState('');
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activar, setActivar] = useState<{ code: string; employeeId: number; asignado: boolean; mensaje: string } | null>(null);

  const cargar = useCallback(() => {
    void direccionApi.recursos().then(setDatos).catch((e: Error) => setError(e.message));
    void direccionApi.operarios().then((r) => setEmpleados(r.operarios)).catch(() => undefined);
  }, []);
  useEffect(cargar, [cargar]);

  if (!datos) return <Empty icon="⏳" title="Cargando recursos…" />;
  if (datos.recursos.length === 0) {
    return (
      <Empty
        icon="🔧"
        title="No hay recursos de mano de obra en SAP"
        body="Créalos en SAP (Recursos → Datos maestros del recurso, tipo Trabajo). Son los que se asignan a las fases de la lista de materiales y de la OF."
      />
    );
  }

  const texto = busqueda.trim().toLowerCase();
  const visibles = empleados.filter(
    (e) => !texto || e.fullName.toLowerCase().includes(texto) || String(e.employeeId).includes(texto),
  );

  const alternar = async (code: string, employeeId: number, asignado: boolean, forzarActivacion = false) => {
    setOcupado(`${code}:${employeeId}`);
    setError(null);
    try {
      const r = await direccionApi.asignarRecurso(code, employeeId, asignado, forzarActivacion);
      setDatos((d) => (d ? { ...d, recursos: r.recursos } : d));
      setActivar(null);
      void direccionApi.operarios().then((x) => setEmpleados(x.operarios));
    } catch (err) {
      // SAP no deja guardar si el recurso tiene Activo e Inactivo en No: se
      // pregunta antes de cambiarle el estado a un dato maestro suyo.
      if (err instanceof ApiError && (err.details as { motivo?: string })?.motivo === 'recurso-sin-activar') {
        setActivar({ code, employeeId, asignado, mensaje: err.message });
      } else {
        setError(err instanceof ApiError ? err.message : String(err));
      }
    } finally {
      setOcupado(null);
    }
  };

  const rolJefe = Number.parseInt(datos.rolJefe, 10);
  const nombreRol = datos.roles.find((r) => r.id === rolJefe)?.name;

  return (
    <section className="card stack">
      <div className="row row--wrap">
        <div>
          <div className="eyebrow">Recursos de mano de obra</div>
          <div className="muted small">
            Marca en qué recursos trabaja cada empleado. Se guarda en SAP (Recursos → pestaña Empleados).
          </div>
        </div>
        <div className="spacer" />
        <input
          className="input"
          style={{ width: 260 }}
          placeholder="Buscar empleado"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
        />
      </div>

      {error ? <div className="toast toast--error" style={{ boxShadow: 'none' }}>{error}</div> : null}

      <div style={{ overflowX: 'auto' }}>
        <table className="table table--zebra">
          <thead>
            <tr>
              <th>Empleado</th>
              {datos.recursos.map((r) => (
                <th key={r.code} style={{ textAlign: 'center' }}>
                  {r.name}
                  <div className="mono small" style={{ textTransform: 'none', letterSpacing: 0 }}>{r.code}</div>
                </th>
              ))}
              <th style={{ textAlign: 'center' }}>
                Jefe de producción
                {nombreRol ? <div className="small" style={{ textTransform: 'none', letterSpacing: 0 }}>rol «{nombreRol}»</div> : null}
              </th>
            </tr>
          </thead>
          <tbody>
            {visibles.map((emp) => (
              <tr key={emp.employeeId}>
                <td>
                  {emp.fullName}
                  <div className="small muted">nº {emp.employeeId} · {emp.jobTitle ?? ''}</div>
                </td>
                {datos.recursos.map((r) => {
                  const asignado = r.employees.includes(emp.employeeId);
                  const clave = `${r.code}:${emp.employeeId}`;
                  return (
                    <td key={r.code} style={{ textAlign: 'center' }}>
                      <button
                        className={`chip${asignado ? ' chip--selected' : ''}`}
                        style={{ minHeight: 40, minWidth: 62 }}
                        disabled={!puedeGestionar || ocupado === clave}
                        onClick={() => void alternar(r.code, emp.employeeId, !asignado)}
                      >
                        {ocupado === clave ? '…' : asignado ? 'Sí' : '—'}
                      </button>
                    </td>
                  );
                })}
                <td style={{ textAlign: 'center' }}>
                  <button
                    className={`chip${emp.supervisor ? ' chip--selected' : ''}`}
                    style={{ minHeight: 40, minWidth: 62 }}
                    disabled={!puedeGestionar || !Number.isFinite(rolJefe) || ocupado === `rol:${emp.employeeId}`}
                    onClick={() => {
                      setOcupado(`rol:${emp.employeeId}`);
                      setError(null);
                      void direccionApi
                        .rolJefe(emp.employeeId, !emp.supervisor)
                        .then(() => direccionApi.operarios())
                        .then((x) => setEmpleados(x.operarios))
                        .catch((e: Error) => setError(e.message))
                        .finally(() => setOcupado(null));
                    }}
                  >
                    {ocupado === `rol:${emp.employeeId}` ? '…' : emp.supervisor ? 'Sí' : '—'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="muted small" style={{ margin: 0 }}>
        Un operario solo puede imputar en una fase si en ella hay un recurso que le incluya. El rol de jefe de
        producción es el que permite finalizar fases y cerrar órdenes.
      </p>

      {activar ? (
        <Dialog title="SAP no deja guardar este recurso" onClose={() => setActivar(null)}>
          <div className="stack">
            <p style={{ margin: 0 }}>{activar.mensaje}</p>
            <p className="muted small" style={{ margin: 0 }}>
              Marcarlo como Activo es un cambio en el dato maestro del recurso en SAP, no solo en el terminal. Queda
              registrado en la auditoría.
            </p>
            <div className="row">
              <button className="btn" onClick={() => setActivar(null)}>Lo corrijo yo en SAP</button>
              <div className="spacer" />
              <button
                className="btn btn--primary btn--lg"
                onClick={() => void alternar(activar.code, activar.employeeId, activar.asignado, true)}
              >
                Marcarlo como Activo y guardar
              </button>
            </div>
          </div>
        </Dialog>
      ) : null}
    </section>
  );
}

/**
 * Histórico del terminal clásico.
 *
 * El add-on de 2018 lleva años anotando quién trabajó en qué OF y en qué fase.
 * Eso no está en los documentos de SAP —ahí solo queda el total emitido por
 * recurso— y es justo lo que el panel necesita para tener algo que enseñar
 * desde el primer día.
 */
function Historico({ puedeGestionar }: { puedeGestionar: boolean }) {
  const [resumen, setResumen] = useState<Awaited<ReturnType<typeof direccionApi.historico>>['resumen'] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [trayendo, setTrayendo] = useState(false);
  const [resultado, setResultado] = useState<string | null>(null);

  const cargar = useCallback(
    () => void direccionApi.historico().then((r) => setResumen(r.resumen)).catch((e: Error) => setError(e.message)),
    [],
  );
  useEffect(cargar, [cargar]);

  if (error) return <div className="toast toast--error" style={{ boxShadow: 'none' }}>{error}</div>;
  if (!resumen) return <Empty icon="⏳" title="Mirando qué hay en SAP…" />;

  if (resumen.registros === 0) {
    return (
      <Empty
        icon="📭"
        title="El terminal clásico no tiene histórico en este SAP"
        body="La tabla @ECI_TOFFTEM está vacía. Los datos del panel se irán llenando según se use el terminal nuevo."
      />
    );
  }

  const fecha = (iso: string | null) =>
    iso ? new Date(iso).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' }) : '—';
  const pendientes = resumen.registros - resumen.yaImportados;

  return (
    <section className="card stack">
      <div className="eyebrow">Histórico del terminal clásico</div>
      <p className="muted small" style={{ margin: 0 }}>
        En la tabla <span className="mono">@ECI_TOFFTEM</span> de SAP están los tramos de trabajo que registró el add-on
        anterior. Traerlos aquí hace que el panel de dirección tenga historia desde el primer día, en vez de empezar a
        cero.
      </p>

      <div className="kpi">
        <div className="kpi__card">
          <div className="kpi__label">Tramos de trabajo</div>
          <div className="kpi__value">{resumen.registros}</div>
          <div className="small muted" style={{ marginTop: 8 }}>
            {resumen.yaImportados > 0 ? `${resumen.yaImportados} ya traídos` : 'ninguno traído todavía'}
          </div>
        </div>
        <div className="kpi__card">
          <div className="kpi__label">Operarios</div>
          <div className="kpi__value">{resumen.operarios}</div>
        </div>
        <div className="kpi__card">
          <div className="kpi__label">Órdenes</div>
          <div className="kpi__value">{resumen.ordenes}</div>
        </div>
        <div className="kpi__card">
          <div className="kpi__label">Paradas con causa</div>
          <div className="kpi__value">{resumen.conCausa}</div>
        </div>
      </div>

      <div className="row row--wrap small muted" style={{ gap: 14 }}>
        <span>Desde <strong>{fecha(resumen.desde)}</strong> hasta <strong>{fecha(resumen.hasta)}</strong></span>
        <span>· {resumen.fases.length} fases: {resumen.fases.slice(0, 6).join(', ')}{resumen.fases.length > 6 ? '…' : ''}</span>
      </div>

      {resumen.descartados.length ? (
        <div className="toast" style={{ boxShadow: 'none', borderColor: 'rgba(251,191,36,.5)' }}>
          No se traerán algunos registros por estar incompletos:{' '}
          {resumen.descartados.map((d) => `${d.registros} ${d.motivo}`).join(' · ')}
        </div>
      ) : null}

      {resultado ? <div className="toast toast--ok" style={{ boxShadow: 'none' }}>{resultado}</div> : null}

      <p className="muted small" style={{ margin: 0 }}>
        Esos tiempos ya se emitieron a SAP en su momento, así que se guardan como cerrados: no se vuelven a contabilizar
        ni aparecen en la cola de envíos. Traerlo dos veces no duplica nada.
      </p>

      <div className="row">
        <div className="spacer" />
        <button
          className="btn btn--primary btn--lg"
          disabled={!puedeGestionar || trayendo || pendientes === 0}
          onClick={() => {
            setTrayendo(true);
            setResultado(null);
            void direccionApi
              .importarHistorico()
              .then((r) => {
                setResultado(
                  `${r.importados} tramos traídos${r.repetidos ? ` · ${r.repetidos} ya estaban` : ''}` +
                    `${r.descartados ? ` · ${r.descartados} incompletos` : ''}. Ya se ven en Planta.`,
                );
                cargar();
              })
              .catch((e: Error) => setError(e.message))
              .finally(() => setTrayendo(false));
          }}
        >
          {trayendo ? 'Trayendo…' : pendientes === 0 ? 'Todo traído' : `Traer ${pendientes} tramos`}
        </button>
      </div>
    </section>
  );
}

function Fases({ puedeGestionar }: { puedeGestionar: boolean }) {
  const [datos, setDatos] = useState<Awaited<ReturnType<typeof direccionApi.fases>> | null>(null);
  const [editando, setEditando] = useState<{ code: string; name: string; nueva: boolean } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const cargar = useCallback(
    () => void direccionApi.fases().then(setDatos).catch((e: Error) => setError(e.message)),
    [],
  );
  useEffect(cargar, [cargar]);

  if (!datos) return <Empty icon="⏳" title="Cargando fases…" />;

  return (
    <section className="card stack">
      <div className="row">
        <div>
          <div className="eyebrow">Fases</div>
          <div className="muted small">
            Tabla <span className="mono">@{datos.tabla}</span> de SAP. La fase se pone en las líneas de la lista de
            materiales y se arrastra a la OF.
          </div>
        </div>
        <div className="spacer" />
        {puedeGestionar ? (
          <button className="btn btn--primary" onClick={() => setEditando({ code: '', name: '', nueva: true })}>
            + Nueva fase
          </button>
        ) : null}
      </div>

      {error ? <div className="toast toast--error" style={{ boxShadow: 'none' }}>{error}</div> : null}

      <table className="table table--zebra">
        <thead><tr><th>Código</th><th>Nombre</th>{puedeGestionar ? <th /> : null}</tr></thead>
        <tbody>
          {datos.fases.map((f) => (
            <tr key={f.Code}>
              <td className="mono">{f.Code}</td>
              <td>{f.Name}</td>
              {puedeGestionar ? (
                <td>
                  <div className="row">
                    <button className="btn" onClick={() => setEditando({ code: f.Code, name: f.Name, nueva: false })}>
                      Renombrar
                    </button>
                    <button
                      className="btn btn--ghost"
                      onClick={() =>
                        void direccionApi
                          .borrarFase(f.Code)
                          .then(cargar)
                          .catch((e: Error) => setError(e.message))
                      }
                    >
                      Borrar
                    </button>
                  </div>
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
      {datos.fases.length === 0 ? <p className="muted small">Sin fases. El terminal necesita al menos una.</p> : null}

      {editando ? (
        <Dialog
          title={editando.nueva ? 'Nueva fase' : `Renombrar ${editando.code}`}
          subtitle="Se guarda en la tabla de SAP."
          onClose={() => setEditando(null)}
        >
          <div className="stack">
            {editando.nueva ? (
              <Field label="Código">
                <input
                  className="input mono"
                  autoFocus
                  value={editando.code}
                  onChange={(e) => setEditando({ ...editando, code: e.target.value.toUpperCase() })}
                  placeholder="FASE04"
                />
              </Field>
            ) : null}
            <Field label="Nombre">
              <input
                className="input"
                autoFocus={!editando.nueva}
                value={editando.name}
                onChange={(e) => setEditando({ ...editando, name: e.target.value })}
              />
            </Field>
            <div className="row">
              <button className="btn" onClick={() => setEditando(null)}>Cancelar</button>
              <div className="spacer" />
              <button
                className="btn btn--primary btn--lg"
                disabled={!editando.code || !editando.name}
                onClick={() =>
                  void (editando.nueva
                    ? direccionApi.crearFase(editando.code, editando.name)
                    : direccionApi.editarFase(editando.code, editando.name)
                  )
                    .then(() => {
                      setEditando(null);
                      cargar();
                    })
                    .catch((e: Error) => setError(e.message))
                }
              >
                Guardar en SAP
              </button>
            </div>
          </div>
        </Dialog>
      ) : null}
    </section>
  );
}

function Causas({ puedeGestionar }: { puedeGestionar: boolean }) {
  const [datos, setDatos] = useState<Awaited<ReturnType<typeof direccionApi.causas>> | null>(null);
  const [editando, setEditando] = useState<{ code: string; name: string; tipo: 'F' | 'P'; activa: boolean; nueva: boolean } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const cargar = useCallback(
    () => void direccionApi.causas().then(setDatos).catch((e: Error) => setError(e.message)),
    [],
  );
  useEffect(cargar, [cargar]);

  if (!datos) return <Empty icon="⏳" title="Cargando causas…" />;

  const tipoDe = (c: Record<string, string>) => (c[datos.campoTipo] ?? 'F').toUpperCase().startsWith('P') ? 'P' : 'F';
  const activaDe = (c: Record<string, string>) => (c[datos.campoActivo] ?? 'Y').toUpperCase() !== 'N';

  return (
    <section className="card stack">
      <div className="row">
        <div>
          <div className="eyebrow">Causas de parada</div>
          <div className="muted small">
            Tabla <span className="mono">@{datos.tabla}</span> de SAP. Las de <strong>fase</strong> salen al parar una
            fase; las de <strong>presencia</strong>, al fichar la salida.
          </div>
        </div>
        <div className="spacer" />
        {puedeGestionar ? (
          <button
            className="btn btn--primary"
            onClick={() => setEditando({ code: '', name: '', tipo: 'F', activa: true, nueva: true })}
          >
            + Nueva causa
          </button>
        ) : null}
      </div>

      {error ? <div className="toast toast--error" style={{ boxShadow: 'none' }}>{error}</div> : null}

      <table className="table table--zebra">
        <thead><tr><th>Código</th><th>Nombre</th><th>Sale en</th><th>Estado</th>{puedeGestionar ? <th /> : null}</tr></thead>
        <tbody>
          {datos.causas.map((c) => (
            <tr key={c.Code}>
              <td className="mono">{c.Code}</td>
              <td>{c.Name}</td>
              <td>
                <Pill tone={tipoDe(c) === 'F' ? 'warn' : 'accent'}>
                  {tipoDe(c) === 'F' ? 'Parada de fase' : 'Salida de presencia'}
                </Pill>
              </td>
              <td>{activaDe(c) ? <Pill tone="ok">Activa</Pill> : <Pill>Inactiva</Pill>}</td>
              {puedeGestionar ? (
                <td>
                  <div className="row">
                    <button
                      className="btn"
                      onClick={() =>
                        setEditando({ code: c.Code, name: c.Name, tipo: tipoDe(c), activa: activaDe(c), nueva: false })
                      }
                    >
                      Editar
                    </button>
                    <button
                      className="btn btn--ghost"
                      onClick={() => void direccionApi.borrarCausa(c.Code).then(cargar).catch((e: Error) => setError(e.message))}
                    >
                      Borrar
                    </button>
                  </div>
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>

      {editando ? (
        <Dialog
          title={editando.nueva ? 'Nueva causa de parada' : `Editar ${editando.code}`}
          subtitle="Se guarda en la tabla de SAP."
          onClose={() => setEditando(null)}
        >
          <div className="stack">
            {editando.nueva ? (
              <Field label="Código">
                <input
                  className="input mono"
                  autoFocus
                  value={editando.code}
                  onChange={(e) => setEditando({ ...editando, code: e.target.value })}
                  placeholder="05"
                />
              </Field>
            ) : null}
            <Field label="Nombre">
              <input className="input" value={editando.name} onChange={(e) => setEditando({ ...editando, name: e.target.value })} />
            </Field>
            <Field label="¿Dónde se ofrece?">
              <div className="chips">
                <button
                  className={`chip${editando.tipo === 'F' ? ' chip--selected' : ''}`}
                  onClick={() => setEditando({ ...editando, tipo: 'F' })}
                >
                  Al parar una fase
                </button>
                <button
                  className={`chip${editando.tipo === 'P' ? ' chip--selected' : ''}`}
                  onClick={() => setEditando({ ...editando, tipo: 'P' })}
                >
                  Al fichar la salida
                </button>
              </div>
            </Field>
            <div className="chips">
              <button
                className={`chip${editando.activa ? ' chip--selected' : ''}`}
                onClick={() => setEditando({ ...editando, activa: !editando.activa })}
              >
                {editando.activa ? 'Activa' : 'Inactiva'}
              </button>
            </div>
            <p className="muted small" style={{ margin: 0 }}>
              Una causa de fase bloquea la finalización hasta que alguien arranque y pare sin causa. Es la forma de
              decir que la incidencia está resuelta.
            </p>
            <div className="row">
              <button className="btn" onClick={() => setEditando(null)}>Cancelar</button>
              <div className="spacer" />
              <button
                className="btn btn--primary btn--lg"
                disabled={!editando.code || !editando.name}
                onClick={() =>
                  void direccionApi
                    .guardarCausa(
                      { code: editando.code, name: editando.name, tipo: editando.tipo, activa: editando.activa },
                      !editando.nueva,
                    )
                    .then(() => {
                      setEditando(null);
                      cargar();
                    })
                    .catch((e: Error) => setError(e.message))
                }
              >
                Guardar en SAP
              </button>
            </div>
          </div>
        </Dialog>
      ) : null}
    </section>
  );
}
